import * as yup from "yup";
import { useTranslation } from "react-i18next";
import useBigNumber from "src/shared/services/bigNumber/useBigNumber";

export const appl1930ValidationSchema = () => {
  const { t } = useTranslation();
  const { toBigNumber, multiply, equals, isZero } = useBigNumber();
  const minDate = new Date("1900-01-01");
  const percentageRegex =
    /^((0(\.[0-9]+)?|1)|([0-9]+\/[1-9]+\d*)|(100|[0-9]?[0-9](\.[0-9]+)?)%)$/;

  return yup
    .object()
    .shape({
      coYearsToClose: yup
        .string()
        .nullable()
        .max(
          4,
          t(
            "Το πεδίο Διάρκεια εταιρείας μπορεί να περιλαμβάνει μέχρι 4 χαρακτήρες.",
          ),
        )
        .test(
          "is-positive-integer",
          t("Το πεδίο πρέπει να είναι θετικός ακέραιος και όχι 0."),
          (value) => {
            if (value === null || value === undefined || value === "") {
              return true;
            } else {
              return /^[1-9]\d*?$/.test(value.toString());
            }
          },
        )
        .when("coAoristi", {
          is: true,
          then: (schema) => schema.notRequired().nullable(),
          otherwise: (schema) => schema.nullable(),
        }),
      d50_id: yup.object().required(t("Το πεδίο είναι υποχρεωτικό")),
      fudocPartnerDecision: yup
        .string()
        .required(t("Το πεδίο είναι υποχρεωτικό")),
      docPartnerDecisionDate: yup
        .date()
        .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
        .required(t("Το πεδίο είναι υποχρεωτικό"))
        .min(minDate, t("Η ημερομηνία πρέπει να είναι μετά την 01/01/1900")),
      coZipCode: yup
        .string()
        .max(
          12,
          t(
            "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
          ),
        )
        .nullable(),
      changedArticles: yup.array().of(
        yup.object().shape({
          editedArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
        }),
      ),
      deletedArticles: yup.array().of(
        yup.object().shape({
          deletedArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
        }),
      ),
      newArticles: yup.array().of(
        yup.object().shape({
          addedArticleNum: yup
            .string()
            .required(t("Το πεδίο είναι υποχρεωτικό"))
            .test(
              "is-integer",
              t(
                "Ο αριθμός άρθρου που τροποποιήθηκε πρέπει να είναι αριθμός και το πεδίο δεν μπορεί να είναι κενό (π.χ. 12 και όχι 12α).",
              ),
              (value) =>
                value === undefined ||
                value === null ||
                /^\d+?$/.test(value.toString()),
            ),
        }),
      ),
      newBoardMembers: yup.array().of(
        yup.object().shape({
          fp1Percentage: yup.string().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .matches(
                  percentageRegex,
                  t(
                    "Μη επιτρεπόμενο format ποσοστού (πχ. 0.333 ή 1/3 ή 33.3% με τελεία στα δεκαδικά)",
                  ),
                ),
          }),
          fp1Idiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp1SoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp1JointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Name: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Surname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Fathersname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Doy: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Adt: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema.max(20).required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Odos: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Arithm: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Tk: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2Polh: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2BirthCity: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema.max(
                50,
                t(
                  "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                ),
              ),
          }),
          fp2BirthYear: yup
            .string()
            .transform((_, val) => (val !== "" ? val : null))
            .nullable()
            .notRequired()
            .when("ok", {
              is: "no",
              then: (schema) =>
                schema.test(
                  "four-digit-year",
                  t("Ελέγξτε την ορθότητα των στοιχείων."),
                  (value) =>
                    value === null ||
                    value === undefined ||
                    /^\d{4}$/.test(value),
                ),
            }),
          fp2Percentage: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .matches(
                  percentageRegex,
                  t(
                    "Μη επιτρεπόμενο format ποσοστού (πχ. 0.333 ή 1/3 ή 33.3% με τελεία στα δεκαδικά)",
                  ),
                ),
          }),
          fp2Idiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2SoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          fp2JointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
        }),
      ),
      newLegalEntityMembers: yup.array().of(
        yup.object().shape({
          partnerCompPercentage: yup.string().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .matches(
                  percentageRegex,
                  t(
                    "Μη επιτρεπόμενο format ποσοστού (πχ. 0.333 ή 1/3 ή 33.3% με τελεία στα δεκαδικά)",
                  ),
                ),
          }),
          partnerCompIdiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompSoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompJointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompGemi2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompName2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompDoy2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompArithm2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompPolh2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompDhmos2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema.required(t("Το πεδίο είναι υποχρεωτικό")).shape({
                label: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
                value: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
          }),
          partnerCompPercentage2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .matches(
                  percentageRegex,
                  t(
                    "Μη επιτρεπόμενο format ποσοστού (πχ. 0.333 ή 1/3 ή 33.3% με τελεία στα δεκαδικά)",
                  ),
                ),
          }),
          partnerCompIdiot2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompSoloRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompJointlyRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          partnerCompTk2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),

          newRepresentatives: yup.array().of(
            yup.object().shape({
              fp2CompReprName: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprSurname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprFathersname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprDoy: yup.object().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprAdt: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprOdos: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprArithm: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprTk: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      12,
                      t(
                        "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprBirthCity: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprPolh: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprBirthYear: yup.date().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .required(t("Το πεδίο είναι υποχρεωτικό"))
                    .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία"))
                    .test(
                      "four-decimal-year",
                      t("Ελέγξτε την ορθότητα των στοιχείων."),
                      (value) =>
                        value === undefined ||
                        value === null ||
                        /^\d{4}?$/.test(value.toString()),
                    ),
              }),
            }),
          ),
        }),
      ),
      newBoardManagers: yup.array().of(
        yup.object().shape({
          manFp1EndDate: yup.date().when(["ok", "manFp1EndDateAoristi"], {
            is: (ok: string, manFp1EndDateAoristi: string | undefined) =>
              ok === "ok" && manFp1EndDateAoristi === undefined,
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
            otherwise: (schema) => schema.nullable(),
          }),
          manFp1EndDateAoristi: yup.string(),
          manFp1Idiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp1SoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp1JointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Name: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Surname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Fathersname: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Doy: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Adt: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Odos: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Arithm: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Polh: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2Tk: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2BirthCity: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2BirthYear: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .test(
                  "four-decimal-year",
                  t("Ελέγξτε την ορθότητα των στοιχείων."),
                  (value) =>
                    value === undefined ||
                    value === null ||
                    /^\d{4}?$/.test(value.toString()),
                ),
          }),
          manFp2EndDate: yup.date().when(["ok", "manFp2EndDateAoristi"], {
            is: (ok: string, manFp2EndDateAoristi: string | undefined) => {
              return ok === "no" && manFp2EndDateAoristi === undefined;
            },
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
            otherwise: (schema) => schema.nullable(),
          }),
          manFp2EndDateAoristi: yup.string(),
          manFp2Idiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2SoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manFp2JointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
        }),
      ),
      newPartnerManagers: yup.array().of(
        yup.object().shape({
          manCompStartDate: yup.date().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
          }),
          manCompEndDate: yup.date().when(["ok", "manCompEndDateAoristi"], {
            is: (ok: string, manCompEndDateAoristi: string | undefined) =>
              ok === "ok" && manCompEndDateAoristi === undefined,
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
            otherwise: (schema) => schema.nullable(),
          }),
          manCompEndDateAoristi: yup.string(),
          manCompIdiot: yup.object().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompSoloRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompJointlyRepresentative: yup.number().when(["ok"], {
            is: (ok: string) => ok === "ok",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompGemi2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompName2: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompDoy: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompOdos: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompArithm: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompPolh: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  50,
                  t(
                    "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompDhmos: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema.required(t("Το πεδίο είναι υποχρεωτικό")).shape({
                label: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
                value: yup.string().required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
          }),
          manCompStartDate2: yup.date().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
          }),
          manCompEndDate2: yup.date().when(["ok", "manCompEndDateAoristi2"], {
            is: (ok: string, manCompEndDateAoristi2: string | undefined) =>
              ok === "no" && manCompEndDateAoristi2 === undefined,
            then: (schema) =>
              schema
                .required(t("Το πεδίο είναι υποχρεωτικό"))
                .typeError(t("Το πεδίο πρέπει να είναι ημερομηνία")),
            otherwise: (schema) => schema.nullable(),
          }),
          manCompEndDateAoristi2: yup.string(),
          manCompIdiot2: yup.object().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompTk: yup.string().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) =>
              schema
                .max(
                  12,
                  t(
                    "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                  ),
                )
                .required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompSoloRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          manCompJointlyRepresentative2: yup.number().when(["ok"], {
            is: (ok: string) => ok === "no",
            then: (schema) => schema.required(t("Το πεδίο είναι υποχρεωτικό")),
          }),
          newRepresentativeManagers: yup.array().of(
            yup.object().shape({
              fp2CompReprName: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprSurname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprFathersname: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprDoy: yup.object().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprAdt: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprOdos: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprArithm: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema.required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprPolh: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη. Το πεδίο Πόλη μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprTk: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      12,
                      t(
                        "Ελέγξτε το Τ.Κ.. Το πεδίο Τ.Κ. μπορεί να περιλαμβάνει μέχρι 12 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprBirthCity: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .max(
                      50,
                      t(
                        "Ελέγξτε το πεδίο Πόλη Γέννησης. Το πεδίο Πόλη Γέννησης μπορεί να περιλαμβάνει μέχρι 50 χαρακτήρες.",
                      ),
                    )
                    .required(t("Το πεδίο είναι υποχρεωτικό")),
              }),
              fp2CompReprBirthYear: yup.string().when(["ok"], {
                is: (ok: string) => ok === "no",
                then: (schema) =>
                  schema
                    .required(t("Το πεδίο είναι υποχρεωτικό"))
                    .test(
                      "four-decimal-year",
                      t("Ελέγξτε την ορθότητα των στοιχείων."),
                      (value) =>
                        value === undefined ||
                        value === null ||
                        /^\d{4}?$/.test(value.toString()),
                    ),
              }),
            }),
          ),
        }),
      ),
      i19_nominalprice: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      nominalPrice: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία (πχ. 20000.00 με τελεία στα δεκαδικά)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      i19_shareamount: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      shareAmount: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      i21_capitalstock: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      capitalStock: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      i19_ecsokefalaiikesShareamount: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      ecsokefalaiikesShareamount: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      i21_ecsokefalaiikes: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      ecsokefalaiikesCapital: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      i19_eggiitikesShareamount: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      eggiitikesShareamount: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "is-integer",
          t("Μόνο ακέραιοι αριθμοί επιτρέπονται"),
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+?$/.test(value.toString()),
        ),
      i21_eggiitikes: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
      eggiitikesCapital: yup
        .string()
        .transform((_, val) => (val !== "" ? val : null))
        .nullable()
        .test(
          "two-decimal-places",
          `${t("Το πεδίο πρέπει να είναι αριθμός")}. ${t("Το ποσό πρέπει να έχει μέχρι δύο δεκαδικά ψηφία με τελεία (πχ. 9999.99)")}.`,
          (value) =>
            value === undefined ||
            value === null ||
            /^\d+(\.\d{1,2})?$/.test(value.toString()),
        ),
    })
    .test("capital-match", function (value) {
      const toSafeBigNumber = (val) => {
        const cleanVal = val?.toString()?.trim();
        if (!cleanVal || isNaN(cleanVal)) {
          return toBigNumber("0");
        }
        return toBigNumber(cleanVal);
      };

      const {
        i19_nominalprice,
        nominalPrice,
        i19_shareamount,
        shareAmount,
        i21_capitalstock,
        capitalStock,
        i19_ecsokefalaiikesShareamount,
        ecsokefalaiikesShareamount,
        i21_ecsokefalaiikes,
        ecsokefalaiikesCapital,
        i19_eggiitikesShareamount,
        eggiitikesShareamount,
        i21_eggiitikes,
        eggiitikesCapital,
      } = value;

      const fields = [
        nominalPrice,
        capitalStock,
        i21_ecsokefalaiikes,
        i21_eggiitikes,
      ];

      for (const field of fields) {
        if (!!field && !/^\d+(\.\d{1,2})?$/.test(field?.toString())) {
          return;
        }
      }

      const nominalPriceTest = toSafeBigNumber(
        nominalPrice || i19_nominalprice,
      );
      const capitalStockTest = toSafeBigNumber(
        capitalStock || i21_capitalstock,
      );
      const shareAmountTest = toSafeBigNumber(shareAmount || i19_shareamount);

      if (
        !equals(capitalStockTest, multiply(shareAmountTest, nominalPriceTest))
      ) {
        return this.createError({
          message: t(
            "Το ποσό των κεφαλαιακών εισφορών δεν ταιριάζει με τα στοιχεία μεριδίων.",
          ),
        });
      }

      if (isZero(capitalStockTest) && !isZero(shareAmountTest)) {
        return this.createError({
          message: t(
            "Το ποσό των κεφαλαιακών εισφορών δεν μπορεί να είναι μηδέν, ενώ έχει δηλωθεί πλήθος μεριδίων μεγαλύτερο του μηδενός.",
          ),
        });
      }

      const ecsokefalaiikesCapitalTest = toSafeBigNumber(
        ecsokefalaiikesCapital || i21_ecsokefalaiikes,
      );
      const ecsokefalaiikesShareamountTest = toSafeBigNumber(
        ecsokefalaiikesShareamount || i19_ecsokefalaiikesShareamount,
      );

      if (
        !equals(
          ecsokefalaiikesCapitalTest,
          multiply(ecsokefalaiikesShareamountTest, nominalPriceTest),
        )
      ) {
        {
          return this.createError({
            message: t(
              "Το ποσό των εξωκεφαλαιακών εισφορών δεν ταιριάζει με τα στοιχεία μεριδίων.",
            ),
          });
        }
      }

      if (
        isZero(ecsokefalaiikesCapitalTest) &&
        !isZero(ecsokefalaiikesShareamountTest)
      ) {
        return this.createError({
          message: t(
            "Το ποσό των εξωκεφαλαιακών εισφορών δεν μπορεί να είναι μηδέν, ενώ έχει δηλωθεί πλήθος μεριδίων μεγαλύτερο του μηδενός.",
          ),
        });
      }

      const eggiitikesCapitalTest = toSafeBigNumber(
        eggiitikesCapital || i21_eggiitikes,
      );
      const eggiitikesShareamountTest = toSafeBigNumber(
        eggiitikesShareamount || i19_eggiitikesShareamount,
      );

      if (
        !equals(
          eggiitikesCapitalTest,
          multiply(eggiitikesShareamountTest, nominalPriceTest),
        )
      ) {
        {
          return this.createError({
            message: t(
              "Το ποσό των εγγυητικών εισφορών δεν ταιριάζει με τα στοιχεία μεριδίων.",
            ),
          });
        }
      }

      if (isZero(eggiitikesCapitalTest) && !isZero(eggiitikesShareamountTest)) {
        return this.createError({
          message: t(
            "Το ποσό των εγγυητικών εισφορών δεν μπορεί να είναι μηδέν, ενώ έχει δηλωθεί πλήθος μεριδίων μεγαλύτερο του μηδενός.",
          ),
        });
      }

      return true;
    })
    .test(
      "at-least-one-article",
      t("Πρέπει να εισάγετε αναλυτικά τις τροποποιήσεις του καταστατικού."),
      (value) =>
        (value.changedArticles && value.changedArticles.length > 0) ||
        (value.deletedArticles && value.deletedArticles.length > 0) ||
        (value.newArticles && value.newArticles.length > 0),
    );
};
